import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, CircleCheck, Clock, Home, MessageSquare, PackageCheck, Pencil, Send, Wrench, X } from 'lucide-react';
import { formatDateTime } from '@nu/shared';
import ItemGroups from './ItemGroups.jsx';
import { useDraft } from '../../offline/draft.js';
import { ORDER_STATUS, cancelOrder, confirmCollected, isOpen, placeOrder, useMyOrders } from './orders.js';

// Field: /stock/order (the team's order and where it's got to) and
// /stock/order/edit (place it, or change it until it's collected). One open
// order per team; Nano Fibre picks it at the storeroom chosen here.

const back = (navigate, to = '/stock', label = 'Stock') => (
  <button type="button" className="link-btn back-link" onClick={() => navigate(to)}><ArrowLeft size={18} aria-hidden="true" /> {label}</button>
);

function Loading({ data, status, navigate }) {
  if (!data) return <section className="card">{back(navigate)}<p className="muted">{status === 'error' ? 'Your orders can’t be loaded with no signal yet. Try again when you have signal.' : 'Loading…'}</p></section>;
  if (!data.live) return <Navigate to="/stock" replace />;
  return <section className="card">{back(navigate)}<p>You aren’t in a team, so you can’t order stock. The office puts you in a team on Match-Ups.</p></section>;
}

// Where the order is, as a coloured flag like the count's.
function StatusFlag({ order, sending }) {
  const s = ORDER_STATUS[order.status];
  const rows = [['Order', <strong>{order.ref || 'New'}</strong>], ['From', <strong>{order.storeroomName || '—'}</strong>]];
  if (order.status === 'ordered') rows.push(['Ordered', <>{formatDateTime(order.editedAt || order.orderedAt)}{order.edits ? ' (changed)' : ''}</>]);
  // When and who on their own lines, so neither runs long on a phone.
  const step = (label, at, by) => {
    rows.push([label, formatDateTime(at)]);
    if (by) rows.push([`${label} by`, <strong>{by}</strong>]);
  };
  if (['picked', 'handed_over'].includes(order.status)) step('Picked', order.pickedAt, order.pickedByName);
  if (order.status === 'collected') step('Collected', order.collectedAt, order.collectedByName);
  if (order.status === 'cancelled') step('Cancelled', order.cancelledAt, order.cancelledByName);
  const Icon = s.tone === 'ok' ? PackageCheck : s.tone === 'wait' ? Clock : CircleCheck;
  return (
    <div className={`stock-flag ${s.tone}`} role="status">
      <span className="stock-flag-tag"><Icon size={16} aria-hidden="true" />{sending ? 'Sending…' : s.label}</span>
      <dl className="stock-flag-rows small">{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
    </div>
  );
}

// Straight after placing or changing an order: what happens next, and the
// three things they might want to do now.
function PlacedPopup({ kind, order, me, onClose }) {
  const navigate = useNavigate();
  const ref = useRef(null);
  const [sure, setSure] = useState(false);
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="popup-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="popup popup-form stock-placed" role="dialog" aria-modal="true" aria-labelledby="placed-title" tabIndex={-1} ref={ref}>
        <button type="button" className="popup-x" aria-label="Close" onClick={onClose}><X size={20} aria-hidden="true" /></button>
        <span className="stock-placed-icon" aria-hidden="true"><CircleCheck size={40} /></span>
        <h2 id="placed-title" className="popup-title">{kind === 'changed' ? 'Order changed' : 'Order placed'}</h2>
        <p className="popup-sub">
          {order.ref ? <strong>{order.ref}</strong> : 'Your order'} {kind === 'changed' ? 'has been sent' : 'is on its way'} to <strong>{order.storeroomName}</strong>.
          {' '}You’ll be told in Messages when it’s ready to collect.
        </p>
        <button type="button" className="btn primary popup-btn with-icon" onClick={() => navigate('/', { replace: true })}><Home size={18} aria-hidden="true" /> Back to home</button>
        <Link to="/stock/order/edit" className="btn popup-btn with-icon"><Pencil size={18} aria-hidden="true" /> Change order</Link>
        <button type="button" className={`btn popup-btn with-icon${sure ? ' danger' : ''}`} onClick={() => (sure ? cancelOrder(me, order).then(onClose) : setSure(true))}>
          <X size={18} aria-hidden="true" /> {sure ? 'Tap again to cancel it' : 'Cancel order'}
        </button>
      </div>
    </div>
  );
}

// An order's lines: what was asked for and, once picked, what was picked.
function OrderLines({ order }) {
  const picked = order.lines.some((l) => l.qtyPicked != null) && order.status !== 'ordered';
  return (
    <div className="stock-items">
      {order.lines.map((l) => {
        const short = picked && l.qtyPicked != null && l.qtyPicked < l.qty;
        return (
          <div key={l.itemId} className={`stock-row stock-order-row${picked ? (short ? ' short' : ' filled') : ''}`}>
            <div><strong>{l.description}</strong><div className="small muted">{l.sku}</div></div>
            <div className="stock-qtys">
              <span className="stock-qty"><span>Asked</span><strong>{l.qty}</strong></span>
              {picked && <span className="stock-qty picked"><span>Picked</span><strong>{l.qtyPicked ?? '—'}</strong></span>}
            </div>
            {short && <span className="small stock-short">{l.qty - l.qtyPicked} short</span>}
          </div>
        );
      })}
    </div>
  );
}

// A past order: one line (number, status, date, where, how many items),
// opening to the full order when tapped.
const PILL = { wait: 'status-on_the_way', ok: 'status-completed', done: 'status-not_started' };
function PastOrder({ order }) {
  const [open, setOpen] = useState(false);
  const s = ORDER_STATUS[order.status];
  const when = order.collectedAt || order.cancelledAt || order.orderedAt;
  return (
    <li className={`stock-past${open ? ' open' : ''}`}>
      <button type="button" className="stock-past-head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="stock-past-main">
          <strong>{order.ref || 'Order'}</strong>
          <span className={`status ${PILL[s.tone]}`}>{s.label}</span>
        </span>
        <span className="small muted">{formatDateTime(when).slice(0, 10)} · {order.storeroomName || '—'} · {order.lines.length} item{order.lines.length === 1 ? '' : 's'}</span>
        <ChevronDown size={18} aria-hidden="true" className={open ? 'up' : ''} />
      </button>
      {open && (
        <div className="stock-past-body">
          <StatusFlag order={order} />
          <OrderLines order={order} />
          {order.note && <p className="small"><strong>Note:</strong> {order.note}</p>}
        </div>
      )}
    </li>
  );
}

// Stock used on the team's jobs that isn't on an order yet: what the next
// order starts with, to replace it, and which jobs it came from.
function UsedOnJobs({ used, note }) {
  if (!used.length) return null;
  return (
    <div className="stock-used notice-line small">
      <strong className="with-icon"><Wrench size={15} aria-hidden="true" /> Used on your jobs, to replace</strong>
      <ul>
        {used.map((u) => (
          <li key={u.itemId}><strong>{u.qty} × {u.description}</strong> <span className="muted">({u.jobs.map((j) => `${j.reference}${u.jobs.length > 1 ? ` ×${j.qty}` : ''}`).join(', ')})</span></li>
        ))}
      </ul>
      {note && <p className="muted">{note}</p>}
    </div>
  );
}

export function StockOrder({ me }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, status, order, history, used, sending, refused } = useMyOrders();
  const [sure, setSure] = useState(false);
  // 'placed' or 'changed' when we've just come from the form.
  const [popup, setPopup] = useState(location.state?.placed || null);
  const closePopup = () => { setPopup(null); navigate('/stock/order', { replace: true, state: null }); };
  if (!data?.live || !data.team) return <Loading data={data} status={status} navigate={navigate} />;
  if (!order && !history.length) return <Navigate to="/stock/order/edit" replace />;

  const past = history.length > 0 && (
    <section className="card">
      <h2 className="stock-past-title">Past orders</h2>
      <ul className="stock-past-list">{history.map((o) => <PastOrder key={o.id} order={o} />)}</ul>
    </section>
  );

  if (!order) {
    return (
      <div className="stack">
        <section className="card">
          {back(navigate)}
          <h1 className="stock-week">Stock orders</h1>
          {refused && <p className="notice-line notice-bad" role="alert">{refused}</p>}
          <p className="muted">Your team has no open order.</p>
          <UsedOnJobs used={used} note="A new order starts with these." />
          <div className="actions"><Link to="/stock/order/edit" className="btn primary with-icon"><Send size={18} aria-hidden="true" /> New order</Link></div>
        </section>
        {past}
      </div>
    );
  }
  const open = isOpen(order);

  return (
    <div className="stack">
      <section className="card">
        {back(navigate)}
        <h1 className="stock-week">Stock order</h1>
        <StatusFlag order={order} sending={sending} />
        {refused && <p className="notice-line notice-bad" role="alert">{refused}</p>}
        {order.pushError && !order.sent && <p className="notice-line notice-warn small">Not with Nano Fibre yet; it’s being retried.</p>}
        {['picked', 'handed_over'].includes(order.status) && <p className="small">Check it at the counter, then confirm you’ve got it. If something’s missing, the storeroom can pick it again.</p>}
        <OrderLines order={order} />
        {order.note && <p className="small"><strong>Note:</strong> {order.note}</p>}
        {open && <UsedOnJobs used={used} note={['picked', 'handed_over'].includes(order.status) ? 'Not on this order. Collect it first, and these go on your next one, or change it to add them (the storeroom picks it again).' : 'Not on this order yet: Change order adds them.'} />}
        {popup && isOpen(order) && <PlacedPopup kind={popup} order={order} me={me} onClose={closePopup} />}
        <div className="actions stock-order-actions">
          {['picked', 'handed_over'].includes(order.status) && (
            <button type="button" className="btn primary with-icon" disabled={sending} onClick={() => confirmCollected(me, order)}><PackageCheck size={18} aria-hidden="true" /> I’ve collected it</button>
          )}
          {open && <Link to="/stock/order/edit" className="btn with-icon"><Pencil size={18} aria-hidden="true" /> Change order</Link>}
          {open && (
            <button type="button" className={`btn with-icon${sure ? ' danger' : ''}`} disabled={sending} onClick={() => (sure ? cancelOrder(me, order).then(() => setSure(false)) : setSure(true))}>
              <X size={18} aria-hidden="true" /> {sure ? 'Tap again to cancel it' : 'Cancel order'}
            </button>
          )}
        </div>
      </section>
      {past}
    </div>
  );
}

export function StockOrderForm({ me }) {
  const navigate = useNavigate();
  const { data, status, order, history, used } = useMyOrders();
  const editing = isOpen(order) ? order : null;
  // The order starts as it stands (if open) plus the stock used on jobs since.
  const start = useMemo(() => {
    const qty = Object.fromEntries((editing?.lines || []).map((l) => [l.itemId, l.qty]));
    for (const u of used) qty[u.itemId] = (qty[u.itemId] || 0) + u.qty;
    return Object.fromEntries(Object.entries(qty).map(([k, v]) => [k, String(v)]));
  }, [editing, used]);
  // What's typed so far is kept on the phone until it's sent.
  const [draft, saveDraft, clearDraft] = useDraft(`nu.draft.order.${editing?.id || 'new'}`);
  const [busy, setBusy] = useState(false);
  if (!data?.live || !data.team) return <Loading data={data} status={status} navigate={navigate} />;

  const items = data.items || [];
  const values = draft.qty ?? start;
  const set = (id, v) => saveDraft({ qty: { ...values, [id]: v } });
  const chosen = (id) => Number(values[id]) > 0;
  const lines = items.filter((i) => chosen(i.id)).length;
  // The storeroom: as on the open order, else the team's last one.
  const roomId = draft.room ?? editing?.storeroomId ?? history[0]?.storeroomId ?? '';
  const storeroom = data.storerooms.find((s) => s.id === Number(roomId));
  const noteText = draft.note ?? editing?.note ?? '';
  const ready = Boolean(storeroom) && lines > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    await placeOrder(me, {
      id: editing?.id || crypto.randomUUID(), storeroom, items, qty: values, note: noteText.trim(), previous: editing,
      used: used.flatMap((u) => u.jobs.map((j) => ({ bookingId: j.bookingId, itemId: u.itemId }))),
    });
    clearDraft();
    navigate('/stock/order', { state: { placed: editing ? 'changed' : 'placed' } });
  };

  return (
    <section className="card">
      {back(navigate, editing ? '/stock/order' : '/stock', editing ? 'Order' : 'Stock')}
      <h1 className="stock-week">{editing ? `Change ${editing.ref || 'order'}` : 'New stock order'}</h1>
      {editing && ['picked', 'handed_over'].includes(editing.status) && <p className="notice-line notice-warn small">It’s already been picked: changing it sends it back to the storeroom to pick again.</p>}
      <p className="muted small">{data.team.name}: one order for the team, either of you can change it until it’s collected.</p>
      <UsedOnJobs used={used} note={editing ? 'Added to the order below, on top of what was on it.' : 'Already filled in below. Change the amounts or add anything else you need.'} />
      {!data.storerooms.length && <p className="notice-line notice-bad small">No Nano Fibre storerooms have come through yet, so orders can’t be placed. Try again later.</p>}
      <form onSubmit={submit} className="stack">
        <label className="fld">
          Collect from
          <select value={roomId} onChange={(e) => saveDraft({ room: e.target.value })} required>
            <option value="">Choose a storeroom</option>
            {data.storerooms.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <div className="stock-progress">
          <span className={`stock-state ${lines ? 'ok' : 'bad'}`}>{lines ? <CircleCheck size={15} aria-hidden="true" /> : <X size={15} aria-hidden="true" />}{lines} item{lines === 1 ? '' : 's'} on the order</span>
        </div>
        {!items.length && <p>Nothing is set up to be ordered yet.</p>}
        <ItemGroups items={items} values={values} onChange={set} done={chosen} verb="How many to order:" tally={(g) => `${g.filter((x) => chosen(x.id)).length} chosen`} whole={(g) => g.some((x) => chosen(x.id))} />
        <label className="fld">
          <span className="with-icon"><MessageSquare size={16} aria-hidden="true" /> Note <span className="small muted">(optional)</span></span>
          <textarea rows={2} maxLength={1000} value={noteText} onChange={(e) => saveDraft({ note: e.target.value })} placeholder="Anything the storeroom should know" />
        </label>
        <div className="actions">
          <button type="submit" className="btn primary with-icon" disabled={!ready || busy}><Send size={18} aria-hidden="true" /> {editing ? 'Save changes' : 'Place order'}</button>
        </div>
        {!ready && <p className="small muted">{!storeroom ? 'Choose the storeroom you’ll collect from' : 'Add at least one item'}.</p>}
      </form>
    </section>
  );
}
